import React, { useEffect, useMemo, useCallback, memo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Stack,
  Breadcrumbs,
  Link,
  Avatar,
  Chip,
  IconButton,
  Collapse,
  LinearProgress,
  Paper,
  CardActionArea,
  ToggleButton,
  ToggleButtonGroup,
  Button,
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import HomeIcon from "@mui/icons-material/Home"
import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import RefreshIcon from "@mui/icons-material/Refresh"
import FactoryIcon from "@mui/icons-material/Factory"
import AssessmentIcon from "@mui/icons-material/Assessment"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import PendingActionsIcon from "@mui/icons-material/PendingActions"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import InventoryIcon from "@mui/icons-material/Inventory"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import BuildIcon from "@mui/icons-material/Build"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import TaskAltIcon from "@mui/icons-material/TaskAlt"
import GridViewIcon from "@mui/icons-material/GridView"
import ViewListIcon from "@mui/icons-material/ViewList"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"

import {
  fetchSections,
  fetchSectionTasks,
  clearSectionsCache,
} from "store/sections/actions"
import {
  selectSections,
  selectSectionTasks,
  selectSectionsLoading,
  selectTasksLoading,
  selectAllSectionsStats,
} from "store/sections/reducer"

// Memoized Section Card
const SectionCard = memo(({ section, onClick, t }) => {
  const completionRate = section.totalTasks > 0
    ? Math.round((section.finishedTasks / section.totalTasks) * 100)
    : 0

  const getStatusColor = () => {
    if (section.totalTasks === 0) return { bg: "#f3f4f6", color: "#6b7280" }
    if (completionRate >= 80) return { bg: "#dcfce7", color: "#16a34a" }
    if (completionRate >= 50) return { bg: "#dbeafe", color: "#3b82f6" }
    return { bg: "#fef3c7", color: "#d97706" }
  }

  const statusColor = getStatusColor()

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid #e5e7eb",
        transition: "all 0.2s ease",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          borderColor: "#3b82f6",
          boxShadow: "0 4px 12px rgba(59, 130, 246, 0.15)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <CardActionArea onClick={onClick} sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "stretch" }}>
        <CardContent sx={{ p: 2.5, flex: 1, display: "flex", flexDirection: "column" }}>
          <Stack spacing={2} sx={{ flex: 1 }}>
            {/* Header - Avatar and Name */}
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Avatar
                sx={{
                  bgcolor: statusColor.bg,
                  color: statusColor.color,
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                }}
              >
                <FactoryIcon sx={{ fontSize: 22 }} />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  color="#111827"
                  sx={{
                    lineHeight: 1.3,
                    wordBreak: "break-word",
                  }}
                >
                  {section.name}
                </Typography>
                {section.floor && (
                  <Chip
                    label={`${t("Floor")} ${section.floor}`}
                    size="small"
                    sx={{
                      bgcolor: "#f3f4f6",
                      color: "#6b7280",
                      fontWeight: 500,
                      mt: 0.5,
                      height: 22,
                      fontSize: "0.7rem",
                    }}
                  />
                )}
              </Box>
            </Stack>

            {/* Tasks Count Badge */}
            <Chip
              label={`${section.totalTasks} ${t("Tasks")}`}
              size="small"
              sx={{
                bgcolor: "#e0e7ff",
                color: "#4338ca",
                fontWeight: 600,
                alignSelf: "flex-start",
                height: 24,
                fontSize: "0.75rem",
              }}
            />

            {/* Progress */}
            <Box>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                <Typography variant="caption" color="#6b7280" sx={{ fontSize: "0.7rem" }}>
                  {t("Completion")}
                </Typography>
                <Typography variant="caption" fontWeight={600} color={statusColor.color} sx={{ fontSize: "0.7rem" }}>
                  {completionRate}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={completionRate}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: "#f3f4f6",
                  "& .MuiLinearProgress-bar": {
                    bgcolor: statusColor.color,
                    borderRadius: 3,
                  },
                }}
              />
            </Box>

            {/* Stats - Fixed height boxes */}
            <Grid container spacing={1} sx={{ mt: "auto" }}>
              <Grid item xs={4}>
                <Paper
                  elevation={0}
                  sx={{
                    py: 1.25,
                    px: 0.5,
                    bgcolor: "#fef3c7",
                    borderRadius: 1.5,
                    textAlign: "center",
                    minHeight: 56,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.1rem",
                      fontWeight: 700,
                      color: "#d97706",
                      lineHeight: 1.2,
                    }}
                  >
                    {section.pendingTasks}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      color: "#92400e",
                      lineHeight: 1.2,
                      mt: 0.25,
                    }}
                  >
                    {t("Pending")}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper
                  elevation={0}
                  sx={{
                    py: 1.25,
                    px: 0.5,
                    bgcolor: "#dbeafe",
                    borderRadius: 1.5,
                    textAlign: "center",
                    minHeight: 56,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.1rem",
                      fontWeight: 700,
                      color: "#3b82f6",
                      lineHeight: 1.2,
                    }}
                  >
                    {section.stockReceivedTasks}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      color: "#1e40af",
                      lineHeight: 1.2,
                      mt: 0.25,
                    }}
                  >
                    {t("In Progress")}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper
                  elevation={0}
                  sx={{
                    py: 1.25,
                    px: 0.5,
                    bgcolor: "#dcfce7",
                    borderRadius: 1.5,
                    textAlign: "center",
                    minHeight: 56,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.1rem",
                      fontWeight: 700,
                      color: "#16a34a",
                      lineHeight: 1.2,
                    }}
                  >
                    {section.finishedTasks}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      color: "#166534",
                      lineHeight: 1.2,
                      mt: 0.25,
                    }}
                  >
                    {t("Finished")}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  )
})

// Memoized Analytics Panel
const AnalyticsPanel = memo(({ analytics, expandedPanels, setExpandedPanels, t }) => (
  <Card
    elevation={0}
    sx={{ borderRadius: 3, border: "1px solid #e5e7eb", mb: 3 }}
  >
    <Box
      sx={{
        px: 3,
        py: 2,
        borderBottom: expandedPanels.analytics ? "1px solid #f3f4f6" : "none",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        cursor: "pointer",
        bgcolor: "#f8fafc",
      }}
      onClick={() =>
        setExpandedPanels((prev) => ({
          ...prev,
          analytics: !prev.analytics,
        }))
      }
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar sx={{ bgcolor: "#8b5cf6", color: "#fff", width: 40, height: 40 }}>
          <AssessmentIcon />
        </Avatar>
        <Box>
          <Typography variant="subtitle1" fontWeight={700} color="#111827">
            {t("Sections Analytics")}
          </Typography>
          <Typography variant="caption" color="#6b7280">
            {t("Overview of all production sections and task statuses")}
          </Typography>
        </Box>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        {analytics.pendingTasks > 0 && (
          <Chip
            icon={<PendingActionsIcon sx={{ fontSize: 16 }} />}
            label={`${analytics.pendingTasks} ${t("Pending")}`}
            size="small"
            sx={{ bgcolor: "#fef3c7", color: "#d97706", fontWeight: 500 }}
          />
        )}
        <IconButton size="small">
          {expandedPanels.analytics ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Stack>
    </Box>
    <Collapse in={expandedPanels.analytics}>
      <CardContent sx={{ p: 3 }}>
        <Grid container spacing={2}>
          {/* Total Tasks */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e5e7eb", height: "100%" }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ bgcolor: "#e0e7ff", color: "#4338ca", width: 48, height: 48 }}>
                  <TaskAltIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="#111827">
                    {analytics.totalTasks}
                  </Typography>
                  <Typography variant="caption" color="#6b7280">
                    {t("Total Tasks")}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {/* Active Sections */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e5e7eb", height: "100%" }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ bgcolor: "#dbeafe", color: "#3b82f6", width: 48, height: 48 }}>
                  <FactoryIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="#111827">
                    {analytics.activeSections}
                  </Typography>
                  <Typography variant="caption" color="#6b7280">
                    {t("Active Sections")}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {/* Completion Rate */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e5e7eb", height: "100%" }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  sx={{
                    bgcolor: analytics.completionRate >= 70 ? "#dcfce7" : "#fef3c7",
                    color: analytics.completionRate >= 70 ? "#16a34a" : "#d97706",
                    width: 48,
                    height: 48,
                  }}
                >
                  <TrendingUpIcon />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h4" fontWeight={700} color="#111827">
                    {analytics.completionRate}%
                  </Typography>
                  <Typography variant="caption" color="#6b7280">
                    {t("Completion Rate")}
                  </Typography>
                </Box>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={analytics.completionRate}
                sx={{
                  mt: 2,
                  height: 6,
                  borderRadius: 3,
                  bgcolor: "#f3f4f6",
                  "& .MuiLinearProgress-bar": {
                    bgcolor: analytics.completionRate >= 70 ? "#16a34a" : "#d97706",
                    borderRadius: 3,
                  },
                }}
              />
            </Paper>
          </Grid>

          {/* Status Breakdown */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e5e7eb", height: "100%" }}
            >
              <Typography variant="subtitle2" fontWeight={600} color="#111827" sx={{ mb: 2 }}>
                {t("Status Breakdown")}
              </Typography>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PendingActionsIcon sx={{ fontSize: 16, color: "#d97706" }} />
                    <Typography variant="body2" color="#6b7280">{t("Pending")}</Typography>
                  </Stack>
                  <Chip
                    label={analytics.pendingTasks}
                    size="small"
                    sx={{ bgcolor: "#fef3c7", color: "#d97706", fontWeight: 600, minWidth: 32 }}
                  />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PlayArrowIcon sx={{ fontSize: 16, color: "#3b82f6" }} />
                    <Typography variant="body2" color="#6b7280">{t("In Progress")}</Typography>
                  </Stack>
                  <Chip
                    label={analytics.inProgressTasks}
                    size="small"
                    sx={{ bgcolor: "#dbeafe", color: "#3b82f6", fontWeight: 600, minWidth: 32 }}
                  />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CheckCircleIcon sx={{ fontSize: 16, color: "#16a34a" }} />
                    <Typography variant="body2" color="#6b7280">{t("Finished")}</Typography>
                  </Stack>
                  <Chip
                    label={analytics.finishedTasks}
                    size="small"
                    sx={{ bgcolor: "#dcfce7", color: "#16a34a", fontWeight: 600, minWidth: 32 }}
                  />
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </CardContent>
    </Collapse>
  </Card>
))

const MuiSectionsOverview = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  document.title = t("Sections Overview") + " | FlexFlow ERP"

  // Redux state
  const sections = useSelector(selectSections)
  const tasks = useSelector(selectSectionTasks)
  const sectionsLoading = useSelector(selectSectionsLoading)
  const tasksLoading = useSelector(selectTasksLoading)
  const sectionsWithStats = useSelector(selectAllSectionsStats)

  const [expandedPanels, setExpandedPanels] = useState({
    analytics: true,
  })
  const [viewMode, setViewMode] = useState("cards") // "cards" or "list"

  const loading = sectionsLoading || tasksLoading

  const handleViewChange = useCallback((event, newView) => {
    if (newView !== null) {
      setViewMode(newView)
    }
  }, [])

  // Fetch data on mount
  useEffect(() => {
    dispatch(fetchSections())
    dispatch(fetchSectionTasks())
  }, [dispatch])

  // Refresh data
  const handleRefresh = useCallback(() => {
    dispatch(clearSectionsCache())
    dispatch(fetchSections(true))
    dispatch(fetchSectionTasks(true))
  }, [dispatch])

  // Navigate to section page
  const handleSectionClick = useCallback((sectionName) => {
    navigate(`/single/${sectionName}`)
  }, [navigate])

  // Compute analytics
  const analytics = useMemo(() => {
    const totalTasks = tasks.length
    const pendingTasks = tasks.filter((t) => t.status === "PENDING").length
    const inProgressTasks = tasks.filter((t) => t.status === "STOCK-RECEIVED").length
    const finishedTasks = tasks.filter((t) => t.status === "FINISHED").length
    const activeSections = sectionsWithStats.filter((s) => s.totalTasks > 0).length
    const completionRate = totalTasks > 0 ? Math.round((finishedTasks / totalTasks) * 100) : 0

    return {
      totalTasks,
      pendingTasks,
      inProgressTasks,
      finishedTasks,
      activeSections,
      totalSections: sections.length,
      completionRate,
    }
  }, [tasks, sections, sectionsWithStats])

  // Sort sections by total tasks (active first)
  const sortedSections = useMemo(() => {
    return [...sectionsWithStats].sort((a, b) => b.totalTasks - a.totalTasks)
  }, [sectionsWithStats])

  // DataGrid columns for list view
  const columns = useMemo(() => [
    {
      field: "name",
      headerName: t("Section"),
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <Avatar sx={{ bgcolor: "#e0e7ff", color: "#4338ca", width: 28, height: 28 }}>
            <FactoryIcon sx={{ fontSize: 16 }} />
          </Avatar>
          <Typography variant="body2" fontWeight={600} color="#111827" sx={{ fontSize: "0.8rem" }}>
            {params.value}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "floor",
      headerName: t("Floor"),
      width: 80,
      renderCell: (params) => params.value ? (
        <Chip label={params.value} size="small" sx={{ bgcolor: "#f3f4f6", color: "#6b7280", height: 22, fontSize: "0.7rem" }} />
      ) : "-",
    },
    {
      field: "totalTasks",
      headerName: t("Total"),
      width: 70,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{ bgcolor: "#e0e7ff", color: "#4338ca", fontWeight: 600, height: 22, fontSize: "0.75rem", minWidth: 28 }}
        />
      ),
    },
    {
      field: "pendingTasks",
      headerName: t("Pending"),
      width: 75,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            bgcolor: params.value > 0 ? "#fef3c7" : "#f3f4f6",
            color: params.value > 0 ? "#d97706" : "#6b7280",
            fontWeight: 600,
            height: 22,
            fontSize: "0.75rem",
            minWidth: 28,
          }}
        />
      ),
    },
    {
      field: "stockReceivedTasks",
      headerName: t("Active"),
      width: 70,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            bgcolor: params.value > 0 ? "#dbeafe" : "#f3f4f6",
            color: params.value > 0 ? "#3b82f6" : "#6b7280",
            fontWeight: 600,
            height: 22,
            fontSize: "0.75rem",
            minWidth: 28,
          }}
        />
      ),
    },
    {
      field: "finishedTasks",
      headerName: t("Done"),
      width: 70,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            bgcolor: params.value > 0 ? "#dcfce7" : "#f3f4f6",
            color: params.value > 0 ? "#16a34a" : "#6b7280",
            fontWeight: 600,
            height: 22,
            fontSize: "0.75rem",
            minWidth: 28,
          }}
        />
      ),
    },
    {
      field: "completion",
      headerName: t("Progress"),
      width: 100,
      renderCell: (params) => {
        const rate = params.row.totalTasks > 0
          ? Math.round((params.row.finishedTasks / params.row.totalTasks) * 100)
          : 0
        return (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: "100%" }}>
            <LinearProgress
              variant="determinate"
              value={rate}
              sx={{
                flex: 1,
                height: 6,
                borderRadius: 3,
                bgcolor: "#f3f4f6",
                "& .MuiLinearProgress-bar": {
                  bgcolor: rate >= 80 ? "#16a34a" : rate >= 50 ? "#3b82f6" : "#d97706",
                  borderRadius: 3,
                },
              }}
            />
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "#6b7280", minWidth: 28 }}>
              {rate}%
            </Typography>
          </Stack>
        )
      },
    },
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation()
            handleSectionClick(params.row.name)
          }}
          sx={{
            color: "#6b7280",
            "&:hover": { color: "#3b82f6", bgcolor: "#eff6ff" },
          }}
        >
          <OpenInNewIcon sx={{ fontSize: 18 }} />
        </IconButton>
      ),
    },
  ], [t, handleSectionClick])

  // DataGrid styles
  const dataGridSx = useMemo(() => ({
    border: "none",
    "& .MuiDataGrid-main": {
      overflow: "visible",
    },
    "& .MuiDataGrid-columnHeaders": {
      bgcolor: "#f9fafb",
      borderBottom: "1px solid #e5e7eb",
      minHeight: "44px !important",
      maxHeight: "44px !important",
      lineHeight: "44px",
    },
    "& .MuiDataGrid-columnHeader": {
      padding: "0 8px",
      height: "44px !important",
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 600,
      fontSize: "0.7rem",
      color: "#6b7280",
      textTransform: "uppercase",
    },
    "& .MuiDataGrid-virtualScroller": {
      marginTop: "0 !important",
      overflowX: "hidden",
    },
    "& .MuiDataGrid-virtualScrollerContent": {
      paddingTop: "0 !important",
    },
    "& .MuiDataGrid-row": {
      cursor: "pointer",
      minHeight: "52px !important",
      maxHeight: "52px !important",
      "&:hover": { bgcolor: "#f9fafb" },
      "&:first-of-type": {
        marginTop: "0",
      },
    },
    "& .MuiDataGrid-cell": {
      borderBottom: "1px solid #f3f4f6",
      fontSize: "0.8rem",
      padding: "0 8px",
      display: "flex",
      alignItems: "center",
      minHeight: "52px !important",
      maxHeight: "52px !important",
    },
  }), [])

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
      {/* Header */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e5e7eb", px: 3, py: 2 }}>
        <Container maxWidth={false} disableGutters>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
              <Link
                component="button"
                underline="hover"
                sx={{ display: "flex", alignItems: "center", color: "#6b7280" }}
                onClick={() => navigate("/customers")}
              >
                <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
                {t("Home")}
              </Link>
              <Typography color="#111827" fontWeight={500}>{t("Sections")}</Typography>
            </Breadcrumbs>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={`${sections.length} ${t("Sections")}`}
                sx={{ bgcolor: "#e0e7ff", color: "#4338ca", fontWeight: 500 }}
              />
              <IconButton onClick={handleRefresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Loading indicator */}
      {loading && <LinearProgress sx={{ height: 2 }} />}

      {/* Main Content */}
      <Container maxWidth={false} sx={{ py: 3 }}>
        {/* Analytics Panel */}
        <AnalyticsPanel
          analytics={analytics}
          expandedPanels={expandedPanels}
          setExpandedPanels={setExpandedPanels}
          t={t}
        />

        {/* Sections Header with View Toggle */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={600} color="#111827">
            {t("Production Sections")}
          </Typography>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewChange}
            size="small"
            sx={{
              bgcolor: "#fff",
              "& .MuiToggleButton-root": {
                border: "1px solid #e5e7eb",
                color: "#6b7280",
                px: 2,
                "&.Mui-selected": {
                  bgcolor: "#3b82f6",
                  color: "#fff",
                  "&:hover": { bgcolor: "#2563eb" },
                },
                "&:hover": { bgcolor: "#f9fafb" },
              },
            }}
          >
            <ToggleButton value="cards">
              <GridViewIcon sx={{ mr: 1, fontSize: 18 }} />
              {t("Cards")}
            </ToggleButton>
            <ToggleButton value="list">
              <ViewListIcon sx={{ mr: 1, fontSize: 18 }} />
              {t("List")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {/* Cards View */}
        {viewMode === "cards" && (
          <Grid container spacing={2} alignItems="stretch">
            {sortedSections.map((section) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={section.id} sx={{ display: "flex" }}>
                <SectionCard
                  section={section}
                  onClick={() => handleSectionClick(section.name)}
                  t={t}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* List View */}
        {viewMode === "list" && (
          <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb", overflow: "hidden" }}>
            <DataGrid
              rows={sortedSections}
              columns={columns}
              loading={loading}
              autoHeight
              rowHeight={52}
              columnHeaderHeight={44}
              disableRowSelectionOnClick
              disableColumnMenu
              onRowClick={(params) => handleSectionClick(params.row.name)}
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              sx={dataGridSx}
              localeText={{ noRowsLabel: t("No sections found") }}
            />
          </Card>
        )}

        {/* Empty state */}
        {!loading && sections.length === 0 && (
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid #e5e7eb",
              p: 6,
              textAlign: "center",
            }}
          >
            <Avatar
              sx={{ bgcolor: "#f3f4f6", color: "#9ca3af", width: 64, height: 64, mx: "auto", mb: 2 }}
            >
              <FactoryIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography variant="h6" color="#111827" sx={{ mb: 1 }}>
              {t("No sections found")}
            </Typography>
            <Typography variant="body2" color="#6b7280">
              {t("Configure production sections in Settings to get started")}
            </Typography>
          </Card>
        )}
      </Container>
    </Box>
  )
}

export default memo(MuiSectionsOverview)
